/**
 * vue-i18n@8 业务应用入口模板
 *
 * 来源：qidian-i18n-spec / templates/vue-i18n-setup/index.js
 * 配套规范：docs/00-final-plan.md
 *
 * 标准布局（由 sync-from-spec.sh 自动写入）：
 *
 *   src/
 *     i18n/
 *       index.js                ← 本文件
 *       lang/
 *         zh-CN.json            ← 扁平 JSON 字典（由 extract-to-dict 维护）
 *         en-US.json            ← 同上
 *     utils/
 *       locale-reader.js        ← templates/locale-reader 同步产物
 *       lib-locale-bridge/      ← templates/lib-locale-bridge 同步产物
 *
 * 在入口 main.js 里这样用：
 *
 *   import Vue from 'vue';
 *   import i18n from 'src/i18n';
 *   import { applyLibLocale, onLibLocaleChange } from 'src/utils/lib-locale-bridge/apply-lib-locale';
 *
 *   applyLibLocale();
 *   onLibLocaleChange(() => location.reload());
 *
 *   new Vue({ i18n, render: h => h(App) }).$mount('#app');
 *
 * 设计约束：
 *   - 只管理业务文案；组件库（vhtml / vhtml-market / cusbase 等）文案由各库自治。
 *   - 不引入 @tencent/qd-i18n-runtime；不替换为运行时 npm 包。
 *   - 当前语言主存储是 localStorage['qidianLocale']；多源兼容由 locale-reader.js 负责。
 *   - 切语言默认走整页刷新策略；如需无刷新切换，可在 onLocaleChange 中追加根组件 key 重建逻辑。
 */

import Vue from 'vue';
import VueI18n from 'vue-i18n';
// 业务可按需替换为相对路径，例如：'../utils/locale-reader'
import {
    getQidianLocale,
    setQidianLocale,
    onLocaleChange,
} from 'src/utils/locale-reader';
import zhCN from './lang/zh-CN.json';
import enUS from './lang/en-US.json';
import I18nT from 'src/components/i18n-t';

Vue.use(VueI18n);

// 项目级 <i18n-t> 全局组件：替代 vue-i18n@8 在 vue@2.5.13 下不可用的 <i18n>。
// 详见 src/components/i18n-t/index.js 文件头注释。
Vue.component('i18n-t', I18nT);

const i18n = new VueI18n({
    locale: getQidianLocale(),
    fallbackLocale: 'zh-CN',
    messages: {
        'zh-CN': zhCN,
        'en-US': enUS,
    },
    silentTranslationWarn: true,
    silentFallbackWarn: true,
});

// 订阅运行时语言变更，自动同步 vue-i18n 当前 locale。
onLocaleChange(function (locale) {
    i18n.locale = locale;
});

/**
 * 切换当前语言。
 * 业务页面若希望无刷新切换，调用方需自行 key++ 重建根组件。
 *
 * @param {string} locale 目标语言（'zh-CN' / 'en-US' / 'zh' / 'en' / 'zh-cn' 等都会被规范化）
 * @returns {string}      实际生效的规范化 locale
 */
export function switchLocale(locale) {
    var nextLocale = setQidianLocale(locale);
    i18n.locale = nextLocale;
    return nextLocale;
}

if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
    if (typeof window !== 'undefined') {
        window.__i18n = i18n;
    }
    // eslint-disable-next-line no-console
    console.info('[i18n] initialized, current locale:', i18n.locale);
}

export { getQidianLocale, setQidianLocale, onLocaleChange };
export default i18n;
